import { dictionaryApi } from './dictionaryClient';
import type { Language } from './languageApi';

/** Per-language meaning row for nyanta, sananta, yanganta, or yangluganta arthas. */
export type TingantaDerivativeArtha = {
  id: number;
  tingantaId: number;
  languageId: number;
  text: string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  language?: Language | null;
};

export type CreateTingantaDerivativeArthaBody = {
  tingantaId: number;
  languageId: number;
  text: string;
};

export type PatchTingantaDerivativeArthaBody = Partial<
  Pick<TingantaDerivativeArtha, 'text' | 'languageId'>
>;

const SEGMENTS = {
  nyanta: 'nyanta-artha',
  sananta: 'sananta-artha',
  yanganta: 'yanganta-artha',
  yangluganta: 'yangluganta-artha',
} as const;

export type DerivativeArthaKind = keyof typeof SEGMENTS;

function normalizeList(data: unknown): TingantaDerivativeArtha[] {
  if (Array.isArray(data)) return data as TingantaDerivativeArtha[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: TingantaDerivativeArtha[] }).data;
  }
  return [];
}

export function isTingantaDerivativeArtha(x: unknown): x is TingantaDerivativeArtha {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.tingantaId === 'number' &&
    typeof o.languageId === 'number' &&
    typeof o.text === 'string'
  );
}

export function parseTingantaDerivativeArthas(
  raw: unknown[] | undefined
): TingantaDerivativeArtha[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isTingantaDerivativeArtha);
}

function crud(kind: DerivativeArthaKind) {
  const path = `/${SEGMENTS[kind]}`;
  return {
    async list(tingantaId?: number): Promise<TingantaDerivativeArtha[]> {
      const res = await dictionaryApi.get<unknown>(path, {
        params: tingantaId != null ? { tingantaId } : undefined,
      });
      return normalizeList(res.data);
    },
    async get(id: number): Promise<TingantaDerivativeArtha> {
      const res = await dictionaryApi.get<TingantaDerivativeArtha>(`${path}/${id}`);
      return res.data;
    },
    async create(body: CreateTingantaDerivativeArthaBody): Promise<TingantaDerivativeArtha> {
      const res = await dictionaryApi.post<TingantaDerivativeArtha>(path, body);
      return res.data;
    },
    async update(
      id: number,
      body: PatchTingantaDerivativeArthaBody
    ): Promise<TingantaDerivativeArtha> {
      const res = await dictionaryApi.patch<TingantaDerivativeArtha>(`${path}/${id}`, body);
      return res.data;
    },
    async delete(id: number): Promise<void> {
      await dictionaryApi.delete(`${path}/${id}`);
    },
  };
}

const nyanta = crud('nyanta');
const sananta = crud('sananta');
const yanganta = crud('yanganta');
const yangluganta = crud('yangluganta');

export const listNyantaArthas = nyanta.list.bind(nyanta);
export const getNyantaArtha = nyanta.get.bind(nyanta);
export const createNyantaArtha = nyanta.create.bind(nyanta);
export const updateNyantaArtha = nyanta.update.bind(nyanta);
export const deleteNyantaArtha = nyanta.delete.bind(nyanta);

export const listSanantaArthas = sananta.list.bind(sananta);
export const getSanantaArtha = sananta.get.bind(sananta);
export const createSanantaArtha = sananta.create.bind(sananta);
export const updateSanantaArtha = sananta.update.bind(sananta);
export const deleteSanantaArtha = sananta.delete.bind(sananta);

export const listYangantaArthas = yanganta.list.bind(yanganta);
export const getYangantaArtha = yanganta.get.bind(yanganta);
export const createYangantaArtha = yanganta.create.bind(yanganta);
export const updateYangantaArtha = yanganta.update.bind(yanganta);
export const deleteYangantaArtha = yanganta.delete.bind(yanganta);

export const listYanglugantaArthas = yangluganta.list.bind(yangluganta);
export const getYanglugantaArtha = yangluganta.get.bind(yangluganta);
export const createYanglugantaArtha = yangluganta.create.bind(yangluganta);
export const updateYanglugantaArtha = yangluganta.update.bind(yangluganta);
export const deleteYanglugantaArtha = yangluganta.delete.bind(yangluganta);

export function buildDerivativeArthaPatch(
  prev: TingantaDerivativeArtha,
  next: Pick<TingantaDerivativeArtha, 'text' | 'languageId'>
): PatchTingantaDerivativeArthaBody {
  const patch: PatchTingantaDerivativeArthaBody = {};
  if (prev.languageId !== next.languageId) patch.languageId = next.languageId;
  if (prev.text !== next.text) patch.text = next.text.trim();
  return patch;
}

export const derivativeArthaCrudByKind: Record<
  DerivativeArthaKind,
  ReturnType<typeof crud>
> = {
  nyanta,
  sananta,
  yanganta,
  yangluganta,
};
